import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytesResumable } from "firebase/storage";
import {
  FaBolt,
  FaCalendarAlt,
  FaClock,
  FaGift,
  FaImage,
  FaInfinity,
  FaLayerGroup,
  FaSearch,
  FaStar,
  FaTags,
  FaTicketAlt,
  FaUpload,
} from "react-icons/fa";
import { db, storage } from "../firebase";
import "./createRewardsAndOffer.css";

const AUDIENCE_OPTIONS = [
  ["all", "All App Users"],
  ["users", "Users"],
  ["artists", "Artists"],
  ["mentors", "Mentors"],
  ["event_attendees", "Event Attendees"],
];

const VISIBILITY_OPTIONS = [
  ["public", "Public"],
  ["invite_only", "Invite Only"],
  ["vip", "VIP Only"],
  ["internal", "Internal Drop"],
];

const REWARD_TYPES = [
  ["wallet_credit", "Wallet Credit"],
  ["event_pass", "Event Pass"],
  ["merch", "Merch"],
  ["backstage", "Backstage Access"],
  ["freebie", "Freebie"],
  ["custom", "Custom Reward"],
];

const OFFER_TYPES = [
  ["percentage", "Percentage Off"],
  ["flat", "Flat Discount"],
  ["bundle", "Bundle Offer"],
  ["buy1get1", "Buy 1 Get 1"],
  ["seat_upgrade", "Seat Upgrade"],
  ["custom", "Custom Offer"],
];

export default function CreateRewardsAndOffer() {
  const [primaryTab, setPrimaryTab] = useState("rewards");
  const [subTabs, setSubTabs] = useState({ rewards: "create", offers: "create" });
  const [rewardForm, setRewardForm] = useState(() => createRewardForm());
  const [offerForm, setOfferForm] = useState(() => createOfferForm());
  const [rewardPreview, setRewardPreview] = useState("");
  const [offerPreview, setOfferPreview] = useState("");
  const [rewards, setRewards] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState({ rewards: true, offers: true });
  const [search, setSearch] = useState({ rewards: "", offers: "" });
  const [submitting, setSubmitting] = useState({ rewards: false, offers: false });
  const [uploadProgress, setUploadProgress] = useState({ rewards: 0, offers: 0 });
  const [messages, setMessages] = useState({
    rewards: { error: "", success: "" },
    offers: { error: "", success: "" },
  });
  const syncStateRef = useRef({ event_rewards: false, event_offers: false });

  useEffect(() => {
    if (!rewardForm.imageFile) {
      setRewardPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(rewardForm.imageFile);
    setRewardPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [rewardForm.imageFile]);

  useEffect(() => {
    if (!offerForm.imageFile) {
      setOfferPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(offerForm.imageFile);
    setOfferPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [offerForm.imageFile]);

  useEffect(() => {
    const rewardQuery = query(collection(db, "event_rewards"), orderBy("createdAt", "desc"));
    const offerQuery = query(collection(db, "event_offers"), orderBy("createdAt", "desc"));

    const unsubRewards = onSnapshot(
      rewardQuery,
      (snap) => {
        const next = snap.docs.map((entry) => ({ id: entry.id, ...(entry.data() || {}) }));
        setRewards(next);
        setLoading((prev) => ({ ...prev, rewards: false }));
        void syncCollectionStatuses("event_rewards", next, syncStateRef);
      },
      () => {
        setRewards([]);
        setLoading((prev) => ({ ...prev, rewards: false }));
      }
    );

    const unsubOffers = onSnapshot(
      offerQuery,
      (snap) => {
        const next = snap.docs.map((entry) => ({ id: entry.id, ...(entry.data() || {}) }));
        setOffers(next);
        setLoading((prev) => ({ ...prev, offers: false }));
        void syncCollectionStatuses("event_offers", next, syncStateRef);
      },
      () => {
        setOffers([]);
        setLoading((prev) => ({ ...prev, offers: false }));
      }
    );

    return () => {
      unsubRewards();
      unsubOffers();
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setMessages((prev) => ({
        rewards: prev.rewards.success || prev.rewards.error ? { error: "", success: "" } : prev.rewards,
        offers: prev.offers.success || prev.offers.error ? { error: "", success: "" } : prev.offers,
      }));
    }, 3600);
    return () => window.clearTimeout(timer);
  }, [messages]);

  const rewardStats = useMemo(() => buildStats(rewards), [rewards]);
  const offerStats = useMemo(() => buildStats(offers), [offers]);

  const filteredRewards = useMemo(
    () => filterCampaigns(rewards, search.rewards),
    [rewards, search.rewards]
  );
  const filteredOffers = useMemo(
    () => filterCampaigns(offers, search.offers),
    [offers, search.offers]
  );

  const currentMode = primaryTab === "rewards" ? "reward" : "offer";
  const currentSubTab = subTabs[primaryTab];
  const currentStats = primaryTab === "rewards" ? rewardStats : offerStats;
  const currentItems = primaryTab === "rewards" ? filteredRewards : filteredOffers;
  const currentLoading = primaryTab === "rewards" ? loading.rewards : loading.offers;

  async function handleSubmit(kind, event) {
    event.preventDefault();

    const stateKey = kind === "reward" ? "rewards" : "offers";
    const collectionName = kind === "reward" ? "event_rewards" : "event_offers";
    const form = kind === "reward" ? rewardForm : offerForm;
    const validationError = validateForm(kind, form);
    if (validationError) {
      setMessages((prev) => ({ ...prev, [stateKey]: { error: validationError, success: "" } }));
      return;
    }

    const docRef = doc(collection(db, collectionName));
    const admin = safeEmployee();

    try {
      setSubmitting((prev) => ({ ...prev, [stateKey]: true }));
      setUploadProgress((prev) => ({ ...prev, [stateKey]: 0 }));
      setMessages((prev) => ({ ...prev, [stateKey]: { error: "", success: "" } }));

      const uploaded = await uploadCampaignImage(kind, docRef.id, form.imageFile, (progress) => {
        setUploadProgress((prev) => ({ ...prev, [stateKey]: progress }));
      });

      const payload = buildPayload(kind, form, docRef.id, uploaded, admin);
      await setDoc(docRef, payload);

      setMessages((prev) => ({
        ...prev,
        [stateKey]: { error: "", success: `${kind === "reward" ? "Reward" : "Offer"} saved successfully.` },
      }));

      if (kind === "reward") {
        setRewardForm(createRewardForm());
      } else {
        setOfferForm(createOfferForm());
      }

      setSubTabs((prev) => ({ ...prev, [stateKey]: "manage" }));
    } catch (error) {
      setMessages((prev) => ({
        ...prev,
        [stateKey]: { error: error?.message || "Failed to save. Please try again.", success: "" },
      }));
    } finally {
      setSubmitting((prev) => ({ ...prev, [stateKey]: false }));
      setUploadProgress((prev) => ({ ...prev, [stateKey]: 0 }));
    }
  }

  const createPanel = currentMode === "reward"
    ? renderCreatePanel({
        kind: "reward",
        form: rewardForm,
        setForm: setRewardForm,
        preview: rewardPreview,
        submitting: submitting.rewards,
        uploadProgress: uploadProgress.rewards,
        message: messages.rewards,
        onSubmit: handleSubmit,
      })
    : renderCreatePanel({
        kind: "offer",
        form: offerForm,
        setForm: setOfferForm,
        preview: offerPreview,
        submitting: submitting.offers,
        uploadProgress: uploadProgress.offers,
        message: messages.offers,
        onSubmit: handleSubmit,
      });

  return (
    <div className="ro-page">
      <section className="ro-hero">
        <div className="ro-hero-copy">
          <div className="ro-kicker">Campaign Control</div>
          <h2>Create Rewards and Offer</h2>
          <p>
            Build event rewards and event offers with clean validity windows, lifetime campaigns,
            image uploads, and live Firestore tracking for your team.
          </p>
        </div>

        <div className="ro-hero-card">
          <div className="ro-hero-card-row">
            <span>Rewards table</span>
            <strong>event_rewards</strong>
          </div>
          <div className="ro-hero-card-row">
            <span>Offers table</span>
            <strong>event_offers</strong>
          </div>
          <div className="ro-hero-card-note">
            Validity is driven by the saved date-time window. Lifetime campaigns stay active until you change them.
          </div>
        </div>
      </section>

      <div className="ro-tabbar">
        <button
          type="button"
          className={`ro-tab-btn ${primaryTab === "rewards" ? "active" : ""}`}
          onClick={() => setPrimaryTab("rewards")}
        >
          <FaGift />
          <span>Rewards</span>
        </button>
        <button
          type="button"
          className={`ro-tab-btn ${primaryTab === "offers" ? "active" : ""}`}
          onClick={() => setPrimaryTab("offers")}
        >
          <FaTags />
          <span>Offers</span>
        </button>
      </div>

      <section className="ro-stats">
        <StatCard label={primaryTab === "rewards" ? "Total Rewards" : "Total Offers"} value={currentStats.total} />
        <StatCard label="Live Now" value={currentStats.live} tone="live" />
        <StatCard label="Scheduled" value={currentStats.scheduled} tone="scheduled" />
        <StatCard label="Expired" value={currentStats.expired} tone="expired" />
      </section>

      <div className="ro-subtabs">
        <button
          type="button"
          className={`ro-subtab-btn ${currentSubTab === "create" ? "active" : ""}`}
          onClick={() => setSubTabs((prev) => ({ ...prev, [primaryTab]: "create" }))}
        >
          {primaryTab === "rewards" ? "Create Reward" : "Create Offer"}
        </button>
        <button
          type="button"
          className={`ro-subtab-btn ${currentSubTab === "manage" ? "active" : ""}`}
          onClick={() => setSubTabs((prev) => ({ ...prev, [primaryTab]: "manage" }))}
        >
          {primaryTab === "rewards" ? "Rewards Table" : "Offers Table"}
        </button>
      </div>

      {currentSubTab === "create" ? (
        createPanel
      ) : (
        <section className="ro-manage-card">
          <div className="ro-manage-head">
            <div>
              <h3>{primaryTab === "rewards" ? "Rewards table" : "Offers table"}</h3>
              <p>
                Search campaigns, track status, and watch items shift to expired when the saved validity window ends.
              </p>
            </div>
            <label className="ro-search">
              <FaSearch />
              <input
                type="search"
                placeholder={primaryTab === "rewards" ? "Search rewards" : "Search offers"}
                value={search[primaryTab]}
                onChange={(event) =>
                  setSearch((prev) => ({ ...prev, [primaryTab]: event.target.value }))
                }
              />
            </label>
          </div>

          <div className="ro-table-wrap">
            <table className="ro-table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Event</th>
                  <th>Audience</th>
                  <th>{primaryTab === "rewards" ? "Reward" : "Offer"}</th>
                  <th>Validity</th>
                  <th>Status</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {renderTableBody({
                  items: currentItems,
                  kind: currentMode,
                  loading: currentLoading,
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function renderCreatePanel({
  kind,
  form,
  setForm,
  preview,
  submitting,
  uploadProgress,
  message,
  onSubmit,
}) {
  const isReward = kind === "reward";
  const typeOptions = isReward ? REWARD_TYPES : OFFER_TYPES;
  const typeLabel = isReward ? "Reward Type" : "Offer Type";
  const valueLabel = isReward ? "Reward Value" : "Offer Value";
  const codeLabel = isReward ? "Reward Code" : "Offer Code";
  const inventoryLabel = isReward ? "Reward Inventory" : "Offer Limit";
  const primaryButtonLabel = isReward ? "Save Reward" : "Save Offer";
  const computedStatus = deriveCampaignStatus({
    isLifetime: form.lifetime,
    validFromAt: form.validFrom,
    validUntilAt: form.validUntil,
  });

  return (
    <section className="ro-create-grid">
      <form className="ro-panel ro-form-panel" onSubmit={(event) => onSubmit(kind, event)}>
        <div className="ro-panel-head">
          <div>
            <h3>{isReward ? "Create event reward" : "Create event offer"}</h3>
            <p>Fill the campaign details, upload artwork, and save directly to Firestore.</p>
          </div>
          <span className={`ro-status-badge ${computedStatus}`}>{formatStatusLabel(computedStatus)}</span>
        </div>

        <div className="ro-field-grid">
          <Field label="Campaign Title *">
            <input
              className="ro-input"
              value={form.campaignTitle}
              onChange={(event) => setForm((prev) => ({ ...prev, campaignTitle: event.target.value }))}
              placeholder={isReward ? "Welcome Spin Reward" : "Early Bird Festival Offer"}
            />
          </Field>
          <Field label="Highlight Line">
            <input
              className="ro-input"
              value={form.highlight}
              onChange={(event) => setForm((prev) => ({ ...prev, highlight: event.target.value }))}
              placeholder={isReward ? "Bonus drop for launch week" : "Limited seats discount"}
            />
          </Field>
          <Field label="Event Name *">
            <input
              className="ro-input"
              value={form.eventName}
              onChange={(event) => setForm((prev) => ({ ...prev, eventName: event.target.value }))}
              placeholder="Mumbai Music Weekend"
            />
          </Field>
          <Field label="Event ID / Slug">
            <input
              className="ro-input"
              value={form.eventId}
              onChange={(event) => setForm((prev) => ({ ...prev, eventId: event.target.value }))}
              placeholder="mumbai_music_weekend_2026"
            />
          </Field>
          <Field label={typeLabel}>
            <select
              className="ro-input"
              value={form.type}
              onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))}
            >
              {typeOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label={valueLabel + " *"}>
            <input
              className="ro-input"
              value={form.value}
              onChange={(event) => setForm((prev) => ({ ...prev, value: event.target.value }))}
              placeholder={isReward ? "100 wallet credits" : "25% off on pass"}
            />
          </Field>
          <Field label={codeLabel}>
            <input
              className="ro-input"
              value={form.code}
              onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value }))}
              placeholder={isReward ? "WELCOME100" : "EARLY25"}
            />
          </Field>
          <Field label={inventoryLabel}>
            <input
              className="ro-input"
              type="number"
              min="0"
              value={form.inventory}
              onChange={(event) => setForm((prev) => ({ ...prev, inventory: event.target.value }))}
              placeholder={isReward ? "500" : "200"}
            />
          </Field>
          <Field label="Target Audience">
            <select
              className="ro-input"
              value={form.targetAudience}
              onChange={(event) => setForm((prev) => ({ ...prev, targetAudience: event.target.value }))}
            >
              {AUDIENCE_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Visibility">
            <select
              className="ro-input"
              value={form.visibility}
              onChange={(event) => setForm((prev) => ({ ...prev, visibility: event.target.value }))}
            >
              {VISIBILITY_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="CTA Label">
            <input
              className="ro-input"
              value={form.ctaLabel}
              onChange={(event) => setForm((prev) => ({ ...prev, ctaLabel: event.target.value }))}
              placeholder={isReward ? "Claim Reward" : "Unlock Offer"}
            />
          </Field>
          <Field label="CTA Route">
            <input
              className="ro-input"
              value={form.ctaRoute}
              onChange={(event) => setForm((prev) => ({ ...prev, ctaRoute: event.target.value }))}
              placeholder="/events"
            />
          </Field>

          {isReward ? null : (
            <>
              <Field label="Minimum Spend">
                <input
                  className="ro-input"
                  type="number"
                  min="0"
                  value={form.minSpend}
                  onChange={(event) => setForm((prev) => ({ ...prev, minSpend: event.target.value }))}
                  placeholder="999"
                />
              </Field>
              <Field label="Max Discount">
                <input
                  className="ro-input"
                  type="number"
                  min="0"
                  value={form.maxDiscount}
                  onChange={(event) => setForm((prev) => ({ ...prev, maxDiscount: event.target.value }))}
                  placeholder="400"
                />
              </Field>
            </>
          )}

          <Field label="Description *" full>
            <textarea
              className="ro-textarea"
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              placeholder={
                isReward
                  ? "Tell your team exactly what the attendee gets and how this reward should be used."
                  : "Explain the offer, the hook, and what the audience should do next."
              }
            />
          </Field>

          <Field label="Terms and Conditions" full>
            <textarea
              className="ro-textarea ro-textarea-small"
              value={form.terms}
              onChange={(event) => setForm((prev) => ({ ...prev, terms: event.target.value }))}
              placeholder="Valid on selected events only. Non-transferable. One redemption per user."
            />
          </Field>
        </div>

        <div className="ro-validity-bar">
          <label className={`ro-switch ${form.lifetime ? "active" : ""}`}>
            <input
              type="checkbox"
              checked={form.lifetime}
              onChange={(event) => setForm((prev) => ({ ...prev, lifetime: event.target.checked }))}
            />
            <span className="ro-switch-ui" />
            <span>Lifetime campaign</span>
          </label>

          <div className="ro-validity-note">
            {form.lifetime
              ? "This campaign stays active until your team updates or removes it."
              : "This campaign will move from scheduled to live to expired from the saved date-time window."}
          </div>
        </div>

        <div className="ro-field-grid">
          <Field label="Valid From">
            <input
              className="ro-input"
              type="datetime-local"
              value={form.validFrom}
              onChange={(event) => setForm((prev) => ({ ...prev, validFrom: event.target.value }))}
            />
          </Field>
          <Field label="Valid Till">
            <input
              className="ro-input"
              type="datetime-local"
              value={form.validUntil}
              disabled={form.lifetime}
              onChange={(event) => setForm((prev) => ({ ...prev, validUntil: event.target.value }))}
            />
          </Field>
        </div>

        <Field label="Campaign Image *">
          <label className="ro-upload-box">
            <FaUpload />
            <div>
              <strong>Upload artwork</strong>
              <span>JPG, PNG, or WEBP. We save it with the campaign record.</span>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={(event) =>
                setForm((prev) => ({ ...prev, imageFile: event.target.files?.[0] || null }))
              }
            />
          </label>
        </Field>

        {uploadProgress > 0 && submitting ? (
          <div className="ro-progress-row">Uploading image: {uploadProgress}%</div>
        ) : null}
        {message.error ? <div className="ro-alert error">{message.error}</div> : null}
        {message.success ? <div className="ro-alert success">{message.success}</div> : null}

        <div className="ro-actions">
          <button type="submit" className="ro-primary-btn" disabled={submitting}>
            {submitting ? "Saving..." : primaryButtonLabel}
          </button>
        </div>
      </form>

      <aside className="ro-panel ro-preview-panel">
        <div className="ro-preview-head">
          <div>
            <h3>Live preview</h3>
            <p>Quick visual check before your team publishes this campaign in the app.</p>
          </div>
        </div>

        <div className="ro-preview-card">
          <div className="ro-preview-media">
            {preview ? (
              <img src={preview} alt="Campaign preview" />
            ) : (
              <div className="ro-preview-empty">
                <FaImage />
                <span>Upload campaign artwork to preview it here.</span>
              </div>
            )}
          </div>

          <div className="ro-preview-copy">
            <div className="ro-preview-kicker">
              <span>{isReward ? "Event Reward" : "Event Offer"}</span>
              <strong>{form.highlight || "Headline goes here"}</strong>
            </div>
            <h4>{form.campaignTitle || (isReward ? "Reward title" : "Offer title")}</h4>
            <p>{form.description || "Your description will appear here once you fill the form."}</p>
            <div className="ro-preview-metrics">
              <PreviewTag icon={<FaLayerGroup />} label={form.eventName || "Event name"} />
              <PreviewTag
                icon={isReward ? <FaGift /> : <FaTicketAlt />}
                label={form.value || (isReward ? "Reward value" : "Offer value")}
              />
              <PreviewTag icon={form.lifetime ? <FaInfinity /> : <FaCalendarAlt />} label={buildValidityLabel(form)} />
              <PreviewTag icon={<FaBolt />} label={formatStatusLabel(computedStatus)} />
            </div>
          </div>
        </div>

        <div className="ro-side-list">
          <SideInfo
            icon={<FaClock />}
            title="Lifecycle"
            text="Scheduled items flip to live automatically when the start time arrives, then move to expired after the end time."
          />
          <SideInfo
            icon={<FaStar />}
            title="Firestore sync"
            text={`Saved into ${isReward ? "event_rewards" : "event_offers"} with created-by details and image URL.`}
          />
          <SideInfo
            icon={<FaTags />}
            title="Team ready"
            text="Audience, code, visibility, and CTA fields are all saved so your app side can render the campaign cleanly."
          />
        </div>
      </aside>
    </section>
  );
}

function renderTableBody({ items, kind, loading }) {
  if (loading) {
    return (
      <tr>
        <td colSpan={7} className="ro-empty-cell">Loading campaigns...</td>
      </tr>
    );
  }

  if (!items.length) {
    return (
      <tr>
        <td colSpan={7} className="ro-empty-cell">
          No {kind === "reward" ? "rewards" : "offers"} found yet.
        </td>
      </tr>
    );
  }

  return items.map((item) => {
    const status = deriveCampaignStatus(item);
    return (
      <tr key={item.id}>
        <td>
          <div className="ro-row-title">
            {item.imageUrl ? <img src={item.imageUrl} alt={item.campaignTitle || item.title || "cover"} /> : null}
            <div>
              <strong>{item.campaignTitle || item.title || "Untitled"}</strong>
              <span>{item.highlight || item.code || item.offerCode || item.rewardCode || "-"}</span>
            </div>
          </div>
        </td>
        <td>
          <div className="ro-row-stack">
            <strong>{item.eventName || "-"}</strong>
            <span>{item.eventId || "No event id"}</span>
          </div>
        </td>
        <td>{formatOptionLabel(item.targetAudience, AUDIENCE_OPTIONS)}</td>
        <td>
          <div className="ro-row-stack">
            <strong>{item.value || "-"}</strong>
            <span>{formatTypeLabel(kind, item.type)}</span>
          </div>
        </td>
        <td>{buildValidityText(item)}</td>
        <td>
          <span className={`ro-status-badge ${status}`}>{formatStatusLabel(status)}</span>
        </td>
        <td>{formatDateTime(item.updatedAt || item.createdAt)}</td>
      </tr>
    );
  });
}

function Field({ label, full = false, children }) {
  return (
    <label className={`ro-field ${full ? "full" : ""}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function StatCard({ label, value, tone = "default" }) {
  return (
    <div className={`ro-stat-card ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function PreviewTag({ icon, label }) {
  return (
    <div className="ro-preview-tag">
      {icon}
      <span>{label}</span>
    </div>
  );
}

function SideInfo({ icon, title, text }) {
  return (
    <div className="ro-side-item">
      <div className="ro-side-icon">{icon}</div>
      <div>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
    </div>
  );
}

function createRewardForm() {
  return {
    campaignTitle: "",
    highlight: "",
    eventName: "",
    eventId: "",
    type: "wallet_credit",
    value: "",
    code: "",
    inventory: "",
    targetAudience: "all",
    visibility: "public",
    ctaLabel: "Claim Reward",
    ctaRoute: "/events",
    description: "",
    terms: "",
    lifetime: false,
    validFrom: formatDateInput(new Date()),
    validUntil: formatDateInput(addDays(new Date(), 7)),
    imageFile: null,
  };
}

function createOfferForm() {
  return {
    campaignTitle: "",
    highlight: "",
    eventName: "",
    eventId: "",
    type: "percentage",
    value: "",
    code: "",
    inventory: "",
    targetAudience: "all",
    visibility: "public",
    ctaLabel: "Unlock Offer",
    ctaRoute: "/events",
    description: "",
    terms: "",
    lifetime: false,
    validFrom: formatDateInput(new Date()),
    validUntil: formatDateInput(addDays(new Date(), 7)),
    minSpend: "",
    maxDiscount: "",
    imageFile: null,
  };
}

function validateForm(kind, form) {
  if (!form.campaignTitle.trim()) return "Campaign title is required.";
  if (!form.eventName.trim()) return "Event name is required.";
  if (!form.description.trim()) return "Description is required.";
  if (!form.value.trim()) return `${kind === "reward" ? "Reward" : "Offer"} value is required.`;
  if (!form.imageFile) return "Campaign image is required.";
  if (!form.validFrom) return "Valid from date-time is required.";
  if (!form.lifetime && !form.validUntil) return "Valid till date-time is required or use lifetime.";

  const validFromAt = toDate(form.validFrom);
  const validUntilAt = toDate(form.validUntil);
  if (!validFromAt) return "Valid from date-time is not valid.";
  if (!form.lifetime && !validUntilAt) return "Valid till date-time is not valid.";
  if (!form.lifetime && validUntilAt <= validFromAt) {
    return "Valid till must be later than valid from.";
  }
  return "";
}

function buildPayload(kind, form, id, uploaded, admin) {
  const validFromAt = toDate(form.validFrom);
  const validUntilAt = form.lifetime ? null : toDate(form.validUntil);
  const status = deriveCampaignStatus({
    isLifetime: form.lifetime,
    validFromAt,
    validUntilAt,
  });

  const base = {
    id,
    campaignTitle: form.campaignTitle.trim(),
    highlight: form.highlight.trim(),
    eventName: form.eventName.trim(),
    eventId: form.eventId.trim(),
    type: form.type,
    value: form.value.trim(),
    code: form.code.trim(),
    inventory: toNumberOrNull(form.inventory),
    targetAudience: form.targetAudience,
    visibility: form.visibility,
    ctaLabel: form.ctaLabel.trim(),
    ctaRoute: form.ctaRoute.trim(),
    description: form.description.trim(),
    terms: form.terms.trim(),
    imageUrl: uploaded.imageUrl,
    imagePath: uploaded.imagePath,
    imageName: form.imageFile?.name || "",
    isLifetime: form.lifetime,
    validFromAt,
    validUntilAt,
    expiresAt: validUntilAt,
    status,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: {
      id: admin.docId || "",
      name: admin.fullName || admin.name || admin.mobile || "Apna Artist Team",
      role: admin.role || "",
    },
  };

  if (kind === "offer") {
    return {
      ...base,
      minSpend: toNumberOrNull(form.minSpend),
      maxDiscount: toNumberOrNull(form.maxDiscount),
      collectionType: "event_offer",
    };
  }

  return {
    ...base,
    collectionType: "event_reward",
  };
}

async function uploadCampaignImage(kind, docId, file, onProgress) {
  const folder = kind === "reward" ? "event_rewards" : "event_offers";
  const cleanName = sanitizeFileName(file?.name || "campaign-image");
  const fileRef = storageRef(storage, `${folder}/${docId}/${Date.now()}_${cleanName}`);
  const task = uploadBytesResumable(fileRef, file, {
    contentType: file?.type || "image/jpeg",
    cacheControl: "public,max-age=3600",
  });

  await new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snapshot) => {
        if (!snapshot.totalBytes) return;
        onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
      },
      (error) => reject(error),
      () => resolve()
    );
  });

  const imageUrl = await getDownloadURL(fileRef);
  return { imageUrl, imagePath: fileRef.fullPath };
}

async function syncCollectionStatuses(collectionName, items, syncStateRef) {
  if (syncStateRef.current[collectionName]) return;

  const staleItems = items.filter((item) => {
    const derivedStatus = deriveCampaignStatus(item);
    return String(item.status || "").toLowerCase() !== derivedStatus;
  });

  if (!staleItems.length) return;

  syncStateRef.current[collectionName] = true;
  try {
    await Promise.all(
      staleItems.map((item) => {
        const nextStatus = deriveCampaignStatus(item);
        return updateDoc(doc(db, collectionName, item.id), {
          status: nextStatus,
          updatedAt: serverTimestamp(),
          ...(nextStatus === "expired" ? { expiredAt: serverTimestamp() } : {}),
        });
      })
    );
  } catch {
    // no-op: UI still derives the correct status locally
  } finally {
    syncStateRef.current[collectionName] = false;
  }
}

function filterCampaigns(items, rawQuery) {
  const queryValue = String(rawQuery || "").trim().toLowerCase();
  if (!queryValue) return items;
  return items.filter((item) => {
    const haystack = [
      item.campaignTitle,
      item.eventName,
      item.eventId,
      item.value,
      item.code,
      item.highlight,
      item.targetAudience,
      item.type,
      item.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(queryValue);
  });
}

function buildStats(items) {
  return items.reduce(
    (acc, item) => {
      const status = deriveCampaignStatus(item);
      acc.total += 1;
      if (status === "live") acc.live += 1;
      if (status === "scheduled") acc.scheduled += 1;
      if (status === "expired") acc.expired += 1;
      return acc;
    },
    { total: 0, live: 0, scheduled: 0, expired: 0 }
  );
}

function deriveCampaignStatus(item) {
  const validFromAt = toDate(item.validFromAt || item.validFrom);
  const validUntilAt = toDate(item.validUntilAt || item.validUntil);
  const isLifetime = Boolean(item.isLifetime || item.lifetime);
  const now = Date.now();

  if (validFromAt && validFromAt.getTime() > now) return "scheduled";
  if (!isLifetime && validUntilAt && validUntilAt.getTime() < now) return "expired";
  return "live";
}

function buildValidityLabel(form) {
  if (form.lifetime) return "Lifetime access";
  const from = formatDateTime(form.validFrom);
  const until = formatDateTime(form.validUntil);
  return from && until ? `${from} to ${until}` : "Set validity";
}

function buildValidityText(item) {
  if (item.isLifetime) return "Lifetime";
  const from = formatDateTime(item.validFromAt || item.validFrom);
  const until = formatDateTime(item.validUntilAt || item.validUntil);
  if (!from && !until) return "-";
  if (!from) return `Until ${until}`;
  if (!until) return `From ${from}`;
  return `${from} to ${until}`;
}

function formatStatusLabel(status) {
  if (status === "scheduled") return "Scheduled";
  if (status === "expired") return "Expired";
  return "Live";
}

function formatTypeLabel(kind, value) {
  const options = kind === "reward" ? REWARD_TYPES : OFFER_TYPES;
  return formatOptionLabel(value, options);
}

function formatOptionLabel(value, options) {
  const match = options.find(([key]) => key === value);
  return match ? match[1] : value || "-";
}

function formatDateTime(value) {
  const date = toDate(value);
  if (!date) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatDateInput(value) {
  const date = value instanceof Date ? new Date(value) : new Date();
  const pad = (num) => String(num).padStart(2, "0");
  return [
    date.getFullYear(),
    "-",
    pad(date.getMonth() + 1),
    "-",
    pad(date.getDate()),
    "T",
    pad(date.getHours()),
    ":",
    pad(date.getMinutes()),
  ].join("");
}

function addDays(value, days) {
  const next = new Date(value);
  next.setDate(next.getDate() + days);
  return next;
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === "function") {
    const converted = value.toDate();
    return Number.isNaN(converted?.getTime?.()) ? null : converted;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toNumberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function sanitizeFileName(value) {
  return String(value || "file")
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9._-]/g, "")
    .toLowerCase();
}

function safeEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
